import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FavoritesService } from '../../services/favorites.service';

/**
 * Star toggle for pinning an entity TYPE to the dashboard.
 *
 * Pins are per entity type, not per record, so there is one of these per table rather than one
 * per row. Reads its state from FavoritesService, so a pin made anywhere is reflected everywhere
 * without a refresh.
 */
@Component({
  selector: 'app-favorite-toggle',
  templateUrl: './favorite-toggle.component.html',
  styleUrls: ['./favorite-toggle.component.sass'],
  // One instance per grid row: OnPush keeps rows out of every change-detection pass.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FavoriteToggleComponent implements OnInit, OnDestroy {
  /** Route slug of the entity type, e.g. 'items'. Resolved to a model name server-side. */
  @Input() entity = '';
  /** Optional display label stored with the pin; defaults to the model name server-side. */
  @Input() name = '';
  /** Compact variant for dense contexts. */
  @Input() small = false;

  pinned = false;
  busy = false;

  private destroy$ = new Subject<void>();

  constructor(
    private favorites: FavoritesService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.favorites.favorites$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.pinned = this.favorites.isEntityPinned(this.entity);
        this.cdr.markForCheck();
      });
    void this.favorites.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get disabled(): boolean {
    return this.busy || !this.entity;
  }

  get label(): string {
    return this.pinned
      ? 'Remove this section from dashboard favorites'
      : 'Pin this section to dashboard favorites';
  }

  async toggle(event?: Event): Promise<void> {
    event?.stopPropagation();
    event?.preventDefault();
    if (this.disabled) return;

    this.busy = true;
    this.cdr.markForCheck();
    try {
      await this.favorites.toggleEntity(this.entity, this.name);
    } catch (error) {
      console.error('Failed to toggle favorite', error);
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }
}
